/**
 * appReview.js — SKStoreReviewController rate-prompt with strict
 * anti-annoyance guardrails.
 *
 * Trigger conditions (ALL must be true):
 *   • Native iOS (never on web — no App Store to write to)
 *   • The user has logged in ≥ 3 times TOTAL (lifetime, tracked
 *     across sessions in Capacitor Preferences)
 *   • The current app session has been active ≥ 3 minutes
 *   • We have prompted them fewer than MAX_PROMPTS times ever
 *   • At least MIN_DAYS_BETWEEN_PROMPTS has passed since the last
 *     prompt (defensive — Apple already rate-limits, but we keep our
 *     own guard so users don't feel spammed even between OS resets)
 *
 * Trigger is INTENT-based: `maybeRequestReview()` is called from a few
 * high-affinity moments (page load after a completed flow, tab switch
 * on the athlete network, story publish, etc.). Each call is idempotent
 * — it silently no-ops when any guardrail fails.
 *
 * On the web build every function is a safe no-op.
 */
import { Capacitor } from '@capacitor/core';

const K_LOGIN_COUNT       = 'flyt_review_login_count';
const K_PROMPT_COUNT      = 'flyt_review_prompt_count';
const K_LAST_PROMPT_AT    = 'flyt_review_last_prompt_at';
const K_SESSION_STARTED   = 'flyt_review_session_started_at';

const MIN_LOGIN_COUNT           = 3;
const MIN_SESSION_MS            = 3 * 60 * 1000;      // 3 minutes
const MAX_PROMPTS               = 4;                  // lifetime cap
const MIN_DAYS_BETWEEN_PROMPTS  = 30;                 // ~monthly max

const isIOSNative = () => {
  try { return Capacitor.getPlatform() === 'ios'; }
  catch { return false; }
};

async function _prefs() {
  if (!Capacitor.isNativePlatform()) return null;
  const { Preferences } = await import('@capacitor/preferences');
  return Preferences;
}

async function _read(key) {
  const P = await _prefs();
  if (!P) {
    try { return window.localStorage.getItem(key); }
    catch { return null; }
  }
  const { value } = await P.get({ key });
  return value || null;
}

async function _write(key, value) {
  const P = await _prefs();
  if (!P) {
    try { window.localStorage.setItem(key, String(value)); }
    catch { /* ignore */ }
    return;
  }
  await P.set({ key, value: String(value) });
}

async function _readNumber(key, dflt = 0) {
  const raw = await _read(key);
  const n = raw == null ? NaN : Number(raw);
  return Number.isFinite(n) ? n : dflt;
}

// ── Session bookkeeping ─────────────────────────────────────────
/** Call once at app boot (or on every AuthProvider mount). Stamps a
 *  fresh session start timestamp so the 3-minute window counts from
 *  when the current app boot actually began. */
export async function markSessionStart() {
  await _write(K_SESSION_STARTED, Date.now());
}

/** Call every time a login succeeds. Increments the lifetime login
 *  counter and stamps a session start (which mirrors the flow — a
 *  fresh login means the app is being used right now). */
export async function markLoginSuccess() {
  const current = await _readNumber(K_LOGIN_COUNT, 0);
  await _write(K_LOGIN_COUNT, current + 1);
  await _write(K_SESSION_STARTED, Date.now());
}

// ── The gated review-request itself ─────────────────────────────
/** Silent no-op unless every guardrail passes. Never throws. */
export async function maybeRequestReview() {
  if (!isIOSNative()) return;
  try {
    const loginCount = await _readNumber(K_LOGIN_COUNT, 0);
    if (loginCount < MIN_LOGIN_COUNT) return;

    const promptCount = await _readNumber(K_PROMPT_COUNT, 0);
    if (promptCount >= MAX_PROMPTS) return;

    const sessionStart = await _readNumber(K_SESSION_STARTED, 0);
    if (!sessionStart) return;
    if (Date.now() - sessionStart < MIN_SESSION_MS) return;

    const lastPromptAt = await _readNumber(K_LAST_PROMPT_AT, 0);
    if (lastPromptAt) {
      const daysSince = (Date.now() - lastPromptAt) / (24 * 60 * 60 * 1000);
      if (daysSince < MIN_DAYS_BETWEEN_PROMPTS) return;
    }

    // Lazy-load the plugin so the web build never pulls it in.
    const mod = await import('@capacitor-community/in-app-review');
    const InAppReview = mod.InAppReview || mod.default?.InAppReview || mod.default;
    if (!InAppReview?.requestReview) return;

    await InAppReview.requestReview();

    // Bump counters AFTER we successfully called the plugin. Apple
    // may or may not have actually shown the sheet (they rate-limit
    // client-side), but from our POV we've "spent" one of our 4
    // budgeted prompts either way.
    await _write(K_PROMPT_COUNT, promptCount + 1);
    await _write(K_LAST_PROMPT_AT, Date.now());
  } catch {
    // Silent — review prompts are always best-effort.
  }
}

/** Debug helper — surfaces the current counters. Not called from
 *  production code but handy for QA-mode toggles later. */
export async function getReviewState() {
  return {
    loginCount:   await _readNumber(K_LOGIN_COUNT, 0),
    promptCount:  await _readNumber(K_PROMPT_COUNT, 0),
    lastPromptAt: await _readNumber(K_LAST_PROMPT_AT, 0),
    sessionStart: await _readNumber(K_SESSION_STARTED, 0),
  };
}
